import React, { useState, useMemo } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Layout, Type, Image, ClipboardList, MousePointerClick, Zap, Search, X } from 'lucide-react';
import {
  getElementsForMode,
  getElementsByCategory,
  ELEMENT_CATEGORIES
} from './elementRegistry';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../index';
import './styles/ElementPalette.css';

const ElementPalette = ({ mode = 'page' }) => {
  const [activeCategory, setActiveCategory] = useState('layout');
  const [searchQuery, setSearchQuery] = useState('');

  // Get elements available for current mode
  const allElements = getElementsForMode(mode);

  // FILTER: Only show grid-container from layout, and all content/form/media/action elements
  // Exclude: section, row, column, grid, grid-cell (cells are auto-generated)
  const availableElements = allElements.filter(el => {
    // Keep grid-container from layout
    if (el.type === 'grid-container') return true;
    // Keep all non-layout elements (content, form, media, action, dynamic)
    if (el.category !== ELEMENT_CATEGORIES.LAYOUT) return true;
    // Exclude all other layout elements
    return false;
  });

  // Filter elements by search query
  const filteredElements = useMemo(() => {
    if (!searchQuery.trim()) return availableElements;

    const query = searchQuery.toLowerCase();
    return availableElements.filter(el =>
      el.label.toLowerCase().includes(query) ||
      el.description.toLowerCase().includes(query) ||
      el.type.toLowerCase().includes(query)
    );
  }, [availableElements, searchQuery]);

  // Group elements by category
  const categories = Object.values(ELEMENT_CATEGORIES).filter(category => {
    return filteredElements.some(el => el.category === category);
  });

  return (
    <div className="element-palette">
      <div className="palette-header">
        <h3>Elements</h3>
        <span className="palette-mode-badge">{mode === 'form' ? 'Form Mode' : 'Page Mode'}</span>
      </div>

      {/* Search input */}
      <div className="palette-search">
        <Search size={16} className="palette-search-icon" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
        <input
          type="text"
          className="palette-search-input"
          placeholder="Search elements..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ paddingLeft: '36px' }}
        />
        {searchQuery && (
          <button
            className="palette-search-clear"
            onClick={() => setSearchQuery('')}
            title="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Category tabs */}
      <div className="palette-tabs-wrapper">
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="palette-tabs">
          <TabsList className="palette-categories">
            {categories.map(category => (
              <TabsTrigger key={category} value={category} className="category-tab">
                {formatCategoryName(category)}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Element lists per category */}
          {categories.map(category => (
            <TabsContent key={category} value={category} className="palette-elements">
              {filteredElements
                .filter(el => el.category === category)
                .map((element, index) => (
                  <Draggable
                    key={element.type}
                    draggableId={`palette-${element.type}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`palette-element ${snapshot.isDragging ? 'dragging' : ''}`}
                      >
                        <div className="element-icon">{element.icon}</div>
                        <div className="element-info">
                          <div className="element-label">{element.label}</div>
                          <div className="element-description">{element.description}</div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}

              {filteredElements.filter(el => el.category === category).length === 0 && (
                <div className="palette-empty">
                  {searchQuery ? 'No elements match your search' : 'No elements in this category'}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Helper text */}
      <div className="palette-footer">
        <p className="palette-hint">
          💡 Drag elements onto the canvas to build your {mode === 'form' ? 'form' : 'page'}
        </p>
      </div>
    </div>
  );
};

// Helper function to format category names
const formatCategoryName = (category) => {
  const icons = {
    layout: <Layout size={16} />,
    content: <Type size={16} />,
    media: <Image size={16} />,
    form: <ClipboardList size={16} />,
    action: <MousePointerClick size={16} />,
    dynamic: <Zap size={16} />
  };

  const labels = {
    layout: 'Layout',
    content: 'Content',
    media: 'Media',
    form: 'Form',
    action: 'Actions',
    dynamic: 'Dynamic'
  };

  if (!icons[category]) return category;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {icons[category]}
      <span>{labels[category]}</span>
    </div>
  );
};

export default ElementPalette;
